import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Grid, Button, Divider } from '@mui/material';
import { getUserCart, increamentCart, decreamentCart, DeleteCart, getAllDeliveryPrices } from '../services/Apis';
import useResponsive from '../hooks/useResponsive';
import emptycartpage from "../assets/cartemptybg.webp";
import { useFilter } from '../context/FilterContext';
import { useNavigate } from 'react-router-dom';

const ShippingCart = () => {
    const { isSmallScreen } = useResponsive();
    const [products, setProducts] = useState([]);
    const { token } = useFilter();
    const [, setCartItems] = useState([]);
    const [deliveryPrice, setDeliveryPrice] = useState(0);
    const navigate = useNavigate()

    useEffect(() => {
        const fetchCart = async () => {
            const cart = await getUserCart();
            setCartItems(cart?.cartItems || []);
            const mappedProducts = (cart?.cartItems || []).map(item => ({
                id: item.bookId || item.comboId,
                cartItemId: item.id,
                name: item.title,
                price: Number(item.discountedPrice) || Number(item.originalPrice) || 0,
                quantity: item.quantity,
                image: 
                    typeof item.imageUrl === 'object' && item.imageUrl !== null
                        ? item.imageUrl.imageUrl
                        : item.imageUrl || emptycartpage,
            }));
            setProducts(mappedProducts);
        };
        fetchCart();

        // Fetch delivery price
        const fetchDeliveryPrice = async () => {
            const prices = await getAllDeliveryPrices();
            const active = prices.find(p => p.isActive);
            setDeliveryPrice(active ? active.amount : 0);
        };
        fetchDeliveryPrice();
    }, []);

    // Increment handler
    const handleIncrement = async (cartItemId, currentQuantity) => {
        await increamentCart(cartItemId, { quantity: currentQuantity + 1 });
        const cart = await getUserCart(token);
        setCartItems(cart?.cartItems || []);
        setProducts((cart?.cartItems || []).map(item => ({
            id: item.bookId || item.comboId,
            cartItemId: item.id,
            name: item.title,
            price: Number(item.discountedPrice) || Number(item.originalPrice) || 0,
            quantity: item.quantity,
            image:
                typeof item.imageUrl === 'object' && item.imageUrl !== null
                    ? item.imageUrl.imageUrl
                    : item.imageUrl || emptycartpage,
        })));
    };

    // Decrement handler
    const handleDecrement = async (cartItemId, currentQuantity) => {
        if (currentQuantity <= 1) return;
        await decreamentCart(cartItemId, { quantity: currentQuantity - 1 });
        const cart = await getUserCart(token);
        setCartItems(cart?.cartItems || []);
        setProducts((cart?.cartItems || []).map(item => ({
            id: item.bookId || item.comboId,
            cartItemId: item.id,
            name: item.title,
            price: Number(item.discountedPrice) || Number(item.originalPrice) || 0,
            quantity: item.quantity,
            image:
                typeof item.imageUrl === 'object' && item.imageUrl !== null
                    ? item.imageUrl.imageUrl
                    : item.imageUrl || emptycartpage,
        })));
    };


    const handleDelete = async (cartItemId, bookId, quantity) => {
        const userId = 1; // Replace with actual userId if needed
        await DeleteCart(cartItemId, { userId, bookId, quantity });
        const cart = await getUserCart(token);
        setCartItems(cart?.cartItems || []);
        setProducts((cart?.cartItems || []).map(item => ({
            id: item.bookId || item.comboId,
            cartItemId: item.id,
            name: item.title,
            price: Number(item.discountedPrice) || Number(item.originalPrice) || 0,
            quantity: item.quantity,
            image:
                typeof item.imageUrl === 'object' && item.imageUrl !== null
                    ? item.imageUrl.imageUrl
                    : item.imageUrl || emptycartpage,
        })));
    };

    
    const totalAmount = products.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const grandTotal = totalAmount + deliveryPrice;

    return (
        <Box p={2}>
            <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 2 }}>Shipping cart details</Typography>

            {products.length === 0 ? (
                <Box textAlign="center" mt={4} height={isSmallScreen ? '75vh' : '80vh'} >
                    <Box
                        component="img"
                        src={emptycartpage}
                        alt="Empty Cart"
                        sx={{
                            // height: 'auto',
                            width: { xs: 350, sm: 290, md: 360, lg: 380 },
                            mb: 2
                        }}
                    />
                    <Typography variant="h3" fontWeight="600" gutterBottom>
                        Your Cart is Empty!
                    </Typography>
                    <Typography variant="h5" gutterBottom mb={2} >
                        You have not added any products to your Cart.
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2, backgroundColor: '#CB4422', textTransform: 'none', mb: isSmallScreen ? 4 : 4 }} >
                        Shop Now
                    </Button>
                </Box>
            ) : (
                <>
                    {isSmallScreen ? (
                        <Box>
                            {products.map(product => (
                                <Paper
                                    key={product.id}
                                    sx={{
                                        mb: 2,
                                        p: { xs: 2, sm: 3 },
                                        borderRadius: 3,
                                        background: '#fcf6f5',
                                        position: 'relative',
                                        textAlign: 'center',
                                    }}
                                    elevation={0}
                                >
                                    {/* Delete Icon */}
                                    <IconButton
                                        color="error"
                                        size="medium"
                                        onClick={() => handleDelete(product.cartItemId, product.id, product.quantity)} sx={{
                                            position: 'absolute',
                                            top: 18,
                                            right: 18,
                                            zIndex: 1,
                                        }}
                                    >
                                        <i className="fas fa-trash-alt" />
                                    </IconButton>

                                    {/* Product Image */}
                                    <Box
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            borderRadius: 2,
                                            background: '#e0e0e0',
                                            mx: 'auto',
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={product.image}
                                            alt={product.name}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                            }}
                                        />
                                    </Box>

                                    {/* Product Name */}
                                    <Typography sx={{ fontWeight: 500, fontSize: 16, mb: 1, fontFamily: 'Poppins' }}>
                                        {product.name}
                                    </Typography>

                                    {/* Price */}
                                    <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1, fontFamily: 'Poppins' }}>
                                        ₹ {product.price.toFixed(2)}
                                    </Typography>

                                    {/* Quantity Controls */}
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center',
                                        border: '1px solid #ddd', borderRadius: 2,
                                        px: 1, py: 0.5, mb: 1.5,
                                        background: '#fff', gap: 1,
                                    }}>
                                        <IconButton size="small" sx={{ p: 0.5 }} onClick={() => handleDecrement(product.cartItemId, product.quantity)}>
                                            <i className="fa-solid fa-minus" style={{ fontSize: 15 }} />
                                        </IconButton>
                                        <Typography sx={{ mx: 1, fontWeight: 500, fontSize: 16 }}>{product.quantity}</Typography>
                                        <IconButton size="small" sx={{ p: 0.5 }} onClick={() => handleIncrement(product.cartItemId, product.quantity)}>
                                            <i className="fa-solid fa-plus" style={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Box>

                                    {/* Subtotal */}
                                    <Typography sx={{ fontWeight: 700, fontSize: 17, fontFamily: 'Poppins' }}>
                                        ₹ {(product.price * product.quantity).toFixed(2)}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="medium">
                                <TableHead sx={{ backgroundColor: '#f8c7c3' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, fontSize: 16 }}>Product</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: 16 }}>Price</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: 16 }}>Quantity</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: 16 }}>Subtotal</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: 16 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {products.map(product => (
                                        <TableRow key={product.id}>
                                            <TableCell sx={{ minWidth: 220, padding: 2.5 }}>
                                                <Grid container spacing={2} alignItems="center" wrap="nowrap">
                                                    <Grid item>
                                                        <Box component="img" src={product.image} alt={product.name} sx={{ width: 60, height: 60, borderRadius: 1, objectFit: 'cover', backgroundColor: '#ddd' }} />
                                                    </Grid>
                                                    <Grid item xs>
                                                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: 13, sm: 15 } }}>{product.name}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 13 }}>₹ {product.price.toFixed(2)}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 13 }}>
                                                {/* Quantity Controls */}
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center', border: '1px solid #ddd',
                                                        borderRadius: 2, px: 1, py: 0.5,
                                                        mb: 1.5, background: '#fff', gap: 1,
                                                    }}
                                                >
                                                    <IconButton size="small" sx={{ p: 0.5 }} onClick={() => handleDecrement(product.cartItemId, product.quantity)}>
                                                        <i className="fa-solid fa-minus" style={{ fontSize: 15 }} />
                                                    </IconButton>
                                                    <Typography sx={{ mx: 1, fontWeight: 500, fontSize: 16 }}>{product.quantity}</Typography>
                                                    <IconButton size="small" sx={{ p: 0.5 }} onClick={() => handleIncrement(product.cartItemId, product.quantity)}>
                                                        <i className="fa-solid fa-plus" style={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Box></TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 13 }}>₹ {(product.price * product.quantity).toFixed(2)}</TableCell>
                                            <TableCell align="center">
                                                <IconButton color="error" size="small" onClick={() => handleDelete(product.cartItemId, product.id, product.quantity)}>
                                                    <i className="fas fa-trash-alt" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Cart Totals */}
                    <Box mt={4} sx={{ border: '1px solid #D0D0D0', borderRadius: 2, backgroundColor: '#FAFAFA', maxWidth: 420, ml: isSmallScreen ? 0 : 'auto' }}>
                        <Typography variant="h5" fontWeight="600" mb={2} sx={{ backgroundColor: '#F0F0F0', p: 2, borderRadius: 1 }}>Cart total details</Typography>
                        <Box sx={{ p: 2 }}>
                            <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant='h5'><i className="fas fa-money-bill-wave" style={{ marginRight: 8 }} />Total Amount</Typography>
                                <Typography variant='h6' fontWeight="600">₹ {totalAmount.toFixed(2)}</Typography>
                            </Grid>
                            <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant='h5'><i className="fas fa-shipping-fast" style={{ marginRight: 8 }} />Delivery Charges</Typography>
                                <Typography variant='h6' fontWeight="600">₹ {deliveryPrice.toFixed(2)}</Typography>
                            </Grid>
                            <Divider sx={{ my: 1 }} />
                            <Grid container justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h5" fontWeight="bold">Grand Total</Typography>
                                <Typography variant="h6" fontWeight="bold">₹ {grandTotal.toFixed(2)}</Typography>
                            </Grid>
                            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                                <Button
  variant="contained"
  sx={{ backgroundColor: '#CB4422', color: '#fff', textTransform: 'none', fontWeight: 600, mt: 1 }}
  onClick={() => navigate('/checkout', {
    state: {
      fromCart: true,
      totalAmount,
      grandTotal,
      deliveryPrice
    }
  })}
>
  <i className="fas fa-arrow-right" style={{ marginRight: 8 }} />Proceed to Checkout
</Button>
                            </Box>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default ShippingCart;
